import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import db, { DocumentTemplate, isTemplateOfflineEnabled } from '@/db';
import { findPendingDocumentByTemplate, syncDocument, seedOfflineICodePrefills, deriveFieldPermissionsFromSnapshot } from '@/db/services/document_service';
import { isOnline as isServerOnline } from '@/lib/connectivity';
import { pullTemplate } from '@/db/services/document_template_service';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { Button } from '@mes-amis/galette-design-system';
import ProgramSiteSelector from './ProgramSiteSelector';
import PendingDocumentModal from './modals/PendingDocumentModal';
import { Document } from '@/db/types/document';
import { trackEvent } from '@/db/services/analytics_service';
import type { SuperFormElementConfig } from '@mes-amis/super-form';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

/**
 * Resolve a `prefill` config value to a concrete value.
 *
 * Supported formats:
 *   "current_time"                          → current ISO datetime string
 *   "person.computed_age"                   → lookup path in core_data_snapshot
 *   "{{ 'TBD' }}"                           → literal string between quotes
 *   "{{ person.full_name }}"                → lookup path (strip {{ }})
 *   "{{ client.case_manager.full_name }}"   → lookup path (strip {{ }})
 */
function resolvePrefillValue(
  prefillConfig: string,
  snapshot: Record<string, unknown> | undefined,
): unknown {
  if (prefillConfig === 'current_time') {
    return new Date().toISOString();
  }

  const templateMatch = prefillConfig.match(/^\{\{\s*(.*?)\s*\}\}$/);

  // Handle {{ ... }} wrapper
  if (templateMatch) {
    const inner = templateMatch[1];

    if (inner === 'current_time') {
      return new Date().toISOString();
    }

    // Literal string: {{ 'TBD' }} or {{ "TBD" }}
    const literalMatch = inner.match(/^(['"])(.*)\1$/);
    if (literalMatch) {
      return literalMatch[2];
    }
    // Lookup path inside {{ }}
    return snapshot?.[inner] ?? undefined;
  }

  // Bare lookup path
  return snapshot?.[prefillConfig] ?? undefined;
}

/**
 * Build the initial super_form data for a new document.
 *
 * For each field in the template's form config:
 *   1. If the field's config has a `prefill` property, resolve it.
 *   2. Otherwise, if the field is marked as core data, look up the field name
 *      in the client's core_data_snapshot.
 */
function buildCoreDataPrefill(
  template: DocumentTemplate,
  snapshot: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!template.super_form?.definition) return {};

  const { fields, form } = template.super_form.definition;
  if (!fields || !form) return {};

  const result: Record<string, unknown> = {};

  const walk = (config: Record<string, SuperFormElementConfig | null>) => {
    for (const [key, entry] of Object.entries(config)) {
      if (!entry) continue;

      // 1. Prefer explicit prefill config
      if (typeof entry.prefill === 'string') {
        const value = resolvePrefillValue(entry.prefill, snapshot);
        if (value !== undefined) {
          result[key] = value;
        }
      }
      // 2. Fallback: core_data fields matched by field name in snapshot
      else if (snapshot) {
        if (key in snapshot) result[key] = snapshot[key];
        if (entry.source && entry.source in snapshot) result[key] = snapshot[entry.source];
      }

      // Recurse into FieldGroup children
      if (entry.type === 'FieldGroup' && entry.fields && typeof entry.fields === 'object') {
        walk(entry.fields as Record<string, SuperFormElementConfig | null>);
      }
    }
  };

  walk(form as Record<string, SuperFormElementConfig | null>);
  return result;
}

interface DocumentTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  clientSlug: string;
  // Optional navigation handler for testing
  onNavigate?: (url: string) => void;
}

export default function DocumentTemplateModal({ isOpen, onClose, clientId, clientSlug, onNavigate }: DocumentTemplateModalProps) {
  const navigate = useNavigate();

  // Get current person for organization scoping
  const { currentPerson, organizationId } = useCurrentPerson();

  // Load client for core_data_snapshot prefill
  const client = useLiveQuery(() => db.clients.get(clientId), [clientId]);

  // Use useLiveQuery to get document templates from the database
  const documentTemplates = useLiveQuery(async () => {
    if (!organizationId) return [];

    const allTemplates = await db.documentTemplates.toArray();
    return allTemplates.filter(template =>
      template.organization_id === organizationId
    );
  }, [organizationId]);

  const loadingTemplates = documentTemplates === undefined;
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [creatingDocument, setCreatingDocument] = useState<number | null>(null); // Stores ID of template being created
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Pending document modal state
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingDocument, setPendingDocument] = useState<Document | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);

  // Program/site selection state
  const [selectionStep, setSelectionStep] = useState<'template' | 'program-site'>('template');
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);

  // Filter and sort templates
  const filteredAndSortedTemplates = useMemo(() => {
    if (!documentTemplates) return [];

    // Filter out templates that don't support offline use
    let filtered = documentTemplates.filter(template => isTemplateOfflineEnabled(template));

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query) ||
        template.code?.toLowerCase().includes(query) ||
        template.category?.toLowerCase().includes(query)
      );
    }

    // Sort alphabetically by name
    return [...filtered].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [documentTemplates, searchQuery]);
  
  // Track online status
  const [isOnline, setIsOnline] = useState(true);
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial status
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectionStep('template');
      setSelectedProgramId(null);
      setSelectedSiteId(null);
      setTemplatesError(null);
    }
  }, [isOpen]);

  // Handle ESC key: clear search if focused with text, otherwise close modal
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (
        document.activeElement === searchInputRef.current &&
        searchQuery
      ) {
        setSearchQuery('');
      } else {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, searchQuery]);

  // Refresh templates, programs, and sites when modal opens
  useEffect(() => {
    if (isOpen && isOnline) {
      dispatchDataEvent(DataEvents.REFRESH_TEMPLATES).catch(err => {
        console.error('Failed to auto-refresh templates on open:', err);
      });
      dispatchDataEvent(DataEvents.REFRESH_PROGRAMS).catch(err => {
        console.error('Failed to auto-refresh programs on open:', err);
      });
      dispatchDataEvent(DataEvents.REFRESH_SITES).catch(err => {
        console.error('Failed to auto-refresh sites on open:', err);
      });
    }
  }, [isOpen, isOnline]);

  // Function to create a new document
  const createNewDocument = async (templateId: number, templateName: string, programId?: number | null, siteId?: number | null) => {
    try {
      // Pull the latest template from the server before creating the draft.
      // Skip when offline — pullTemplate would fail fast and `freshTemplate`
      // would be null anyway, so the call is wasted (and adds a noticeable
      // delay on flaky networks where it has to time out first).
      const freshTemplate = isServerOnline() ? await pullTemplate(templateId).catch(() => null) : null;

      // Create a new document record
      const now = new Date().toISOString();
      // Use UUID for both id and local_id - makes it visually clear this is a draft
      const uuid = crypto.randomUUID();
      const localId = `draft-${uuid}`;

      const newDocument = {
        id: uuid, // UUID string for drafts (replaced by numeric server ID on sync)
        local_id: localId, // Prefixed UUID for draft URLs (persists after sync)
        name: templateName || 'Document',
        client_id: clientId,
        template_id: templateId,
        template_label: templateName,
        status: 'draft',
        super_form: (freshTemplate ?? selectedTemplate) ? buildCoreDataPrefill((freshTemplate ?? selectedTemplate)!, client?.core_data_snapshot) : {},
        field_permissions: deriveFieldPermissionsFromSnapshot(client?.core_data_snapshot, (freshTemplate ?? selectedTemplate)?.permissioned_slugs ?? []),
        created_at: now,
        updated_at: now,
        synced: false, // Will be set to true after sync
        ...(programId ? { program_id: programId } : {}),
        ...(siteId ? { site_id: siteId } : {}),
        ...(currentPerson ? {
          creator_id: currentPerson.id,
          creator: { id: currentPerson.id, full_name: currentPerson.official_full_name ?? '' },
        } : {}),
      };

      // Check if a document with this ID already exists (extremely unlikely with UUID)
      const existingDoc = await db.documents.get(uuid);
      if (existingDoc) {
        console.warn(`Document with ID ${uuid} already exists, skipping creation`);
        setCreatingDocument(null);
        return;
      }

      // Store the new document in IndexedDB
      await db.documents.add(newDocument);

      console.log('Created new document:', newDocument);

      // Always seed prefills from cached prior assessments so the editor opens
      // with PARC affordances already painted — no blank-form window. When
      // online we also fire-and-forget a sync; the server's
      // `after_create :seed_unconfirmed_prefills!` runs and the response
      // merges over the local seed via the per-key HLC CRDT (matching
      // `source` strings means no visible flicker). The Dexie write is what
      // the editor subscribes to via useLiveQuery, so we don't need to
      // await the sync before navigation.
      const templateForSeeding = freshTemplate ?? selectedTemplate;
      try {
        if (templateForSeeding) {
          await seedOfflineICodePrefills(uuid, clientId, templateForSeeding);
        }
      } catch (err) {
        console.warn('Local prefill seeding failed for new draft', err);
      }
      if (isServerOnline()) {
        // Re-read from Dexie so syncDocument ships the post-seed snapshot
        // (with `field_prefills` + seeded `super_form`).
        const seeded = (await db.documents.get(uuid)) ?? newDocument;
        void syncDocument(seeded as Document).catch((err) => {
          console.warn('Background sync of new draft failed', err);
        });
      }

      // Track document creation
      await trackEvent('document.created', {
        document_id: localId,
        client_id: clientId,
        template_id: templateId,
        is_offline: true,
      });
      // Reset state
      setCreatingDocument(null);
      setPendingDocument(null);
      setSelectedTemplate(null);

      // Close the modal only after successful creation
      onClose();

      // Navigate to the draft edit page (uses local_id in URL)
      const url = `/clients/${clientSlug}/documents/drafts/${localId}/edit`;

      // Use the onNavigate prop if provided (for testing), otherwise use the router
      if (onNavigate) {
        onNavigate(url);
      } else {
        navigate(url);
      }
    } catch (error) {
      console.error('Error creating document:', error);
      setTemplatesError(`Error creating document: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setCreatingDocument(null);
    }
  };

  // Function to handle selecting a document
  const handleSelectDocument = async (documentId: number, _name: string) => {
    try {
      // Show creating state for the specific template
      setCreatingDocument(documentId);

      // Find the template with the matching ID
      const template = documentTemplates?.find(t => t.id === documentId);

      if (!template) {
        throw new Error(`Template with ID ${documentId} not found`);
      }

      // Check for existing pending document with this template
      const existingPending = await findPendingDocumentByTemplate(clientId, documentId);

      if (existingPending) {
        // Show the pending document modal
        setPendingDocument(existingPending);
        setSelectedTemplate(template);
        setShowPendingModal(true);
        // Reset creating state since we're showing a modal instead
        setCreatingDocument(null);
      } else {
        // No pending document — go to program/site selection
        setSelectedTemplate(template);
        setSelectedProgramId(null);
        setSelectedSiteId(null);
        setSelectionStep('program-site');
        setCreatingDocument(null);
      }
    } catch (error) {
      console.error('Error handling document selection:', error);
      setTemplatesError(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setCreatingDocument(null);
    }
  };

  // Handle opening existing pending document
  const handleOpenExisting = () => {
    if (pendingDocument) {
      // Close the pending modal first
      setShowPendingModal(false);
      // Reset state
      setPendingDocument(null);
      setSelectedTemplate(null);
      // Close the main modal
      onClose();

      // Navigate to the existing document (use draft route for offline-created docs)
      const isDraftDoc = pendingDocument.local_id && typeof pendingDocument.local_id === 'string' && pendingDocument.local_id.startsWith('draft-');
      const url = isDraftDoc
        ? `/clients/${clientSlug}/documents/drafts/${pendingDocument.local_id}/edit`
        : `/clients/${clientSlug}/documents/${pendingDocument.id}/edit`;
      if (onNavigate) {
        onNavigate(url);
      } else {
        navigate(url);
      }
    }
  };

  // Handle creating new document despite pending one existing
  const handleCreateNewFromPending = useCallback(() => {
    if (!selectedTemplate) return;

    // Close the pending modal
    setShowPendingModal(false);
    setPendingDocument(null);

    setSelectedProgramId(null);
    setSelectedSiteId(null);
    setSelectionStep('program-site');
  }, [selectedTemplate]);

  // Handle creating document from the program/site selection step
  const handleCreateWithProgramSite = useCallback(async () => {
    if (!selectedTemplate) return;
    setCreatingDocument(selectedTemplate.id);
    await createNewDocument(selectedTemplate.id, selectedTemplate.name, selectedProgramId, selectedSiteId);
  }, [selectedTemplate, selectedProgramId, selectedSiteId, createNewDocument]);

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">
              {selectionStep === 'template' ? 'Which document do you want to add?' : selectedTemplate?.program_disabled ? 'Select Site' : 'Select Program & Site'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close modal"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {selectionStep === 'template' && (
            /* Search Input */
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search templates by name, description, code, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="p-6 overflow-y-auto flex-grow">
          {selectionStep === 'template' ? (
            <>
              {loadingTemplates ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : templatesError ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  <p>{templatesError}</p>
                </div>
              ) : filteredAndSortedTemplates.length === 0 ? (
                <div className="text-center py-8">
                  {searchQuery ? (
                    <>
                      <p className="text-gray-500 mb-2">No templates match your search</p>
                      <p className="text-gray-400 text-sm">Try a different search term or clear the filter</p>
                    </>
                  ) : (
                    <p className="text-gray-500">No document templates available</p>
                  )}
                </div>
              ) : (
                <>
                  {searchQuery && (
                    <p className="text-sm text-gray-600 mb-3">
                      Found {filteredAndSortedTemplates.length} template{filteredAndSortedTemplates.length !== 1 ? 's' : ''}
                    </p>
                  )}
                  <div className="space-y-3">
                    {filteredAndSortedTemplates.map((template) => (
                    <div
                      key={template.id}
                      className={`border rounded-lg p-4 transition-all ${creatingDocument === template.id ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50 cursor-pointer'}`}
                      onClick={creatingDocument === null ? () => void handleSelectDocument(template.id, template.name) : undefined}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-lg">{template.name}</h4>
                          {template.description && <p className="text-gray-600 text-sm mt-1">{template.description}</p>}
                        </div>
                        <div className="flex items-center">
                          {creatingDocument === template.id && (
                            <div className="mr-2 flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                              <span className="text-blue-600 text-xs">Creating...</span>
                            </div>
                          )}
                          {template.status && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                              {template.status}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2">
                        {template.category && (
                          <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                            {template.category}
                          </span>
                        )}
                        {template.code && (
                          <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                            {template.code}
                          </span>
                        )}
                      </div>
                    </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            /* Program & Site selection step */
            <div className="space-y-6">
              {selectedTemplate && (
                <p className="text-sm text-gray-600">
                  Template: <span className="font-medium">{selectedTemplate.name}</span>
                </p>
              )}

              <ProgramSiteSelector
                clientId={clientId}
                templateId={selectedTemplate?.id}
                selectedProgramId={selectedProgramId}
                selectedSiteId={selectedSiteId}
                onProgramChange={setSelectedProgramId}
                onSiteChange={setSelectedSiteId}
                autoSelect
              />
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          {selectionStep === 'program-site' && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setSelectionStep('template');
                  setSelectedTemplate(null);
                  setSelectedProgramId(null);
                  setSelectedSiteId(null);
                }}
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleCreateWithProgramSite()}
                disabled={creatingDocument !== null}
              >
                {creatingDocument !== null ? 'Creating...' : 'Create Document'}
              </Button>
            </>
          )}
          {selectionStep === 'template' && (
            <Button variant="secondary" onClick={onClose}>Close</Button>
          )}
        </div>
      </div>
      
      {/* Pending Document Modal */}
      <PendingDocumentModal
        isOpen={showPendingModal}
        onClose={() => {
          setShowPendingModal(false);
          setPendingDocument(null);
          setSelectedTemplate(null);
          setCreatingDocument(null);
        }}
        pendingDocument={pendingDocument}
        templateName={selectedTemplate?.name || ''}
        onOpenExisting={handleOpenExisting}
        onCreateNew={handleCreateNewFromPending}
      />
    </div>
  );
}
